import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useGetAdminEnquiriesQuery } from "@/store/adminService";
import { formatAmount } from "@/util/sums";
import { optionLabel, propertyTypeOptions, timeframeOptions } from "../GetStarted/enquiryOptions";

const headerClass = "text-left px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider";

export default function AdminEnquiries() {
  const [page, setPage] = useState(0);
  const navigate = useNavigate();
  const { data, isLoading } = useGetAdminEnquiriesQuery({ page, size: 20 });

  if (isLoading) {
    return <div className="text-center py-8 text-gray-500">Loading enquiries...</div>;
  }

  const enquiries = data?.items || [];

  return (
    <div>
      <h2 className="text-2xl font-semibold text-gray-900 mb-6">Enquiries</h2>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <th className={headerClass}>Name</th>
              <th className={headerClass}>Phone</th>
              <th className={headerClass}>Looking in</th>
              <th className={headerClass}>Target</th>
              <th className={headerClass}>Timeframe</th>
              <th className={headerClass}>Received</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {enquiries.map((enquiry) => (
              <tr
                key={enquiry.id}
                onClick={() => navigate(`/admin/enquiries/${enquiry.id}`)}
                className="cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <td className="px-4 py-3 text-sm text-gray-900">
                  <Link
                    to={`/admin/enquiries/${enquiry.id}`}
                    onClick={(event) => event.stopPropagation()}
                    className="font-medium hover:underline"
                  >
                    {enquiry.firstName} {enquiry.lastName}
                  </Link>
                  <span className="block text-xs text-gray-500">{enquiry.email}</span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">{enquiry.phone}</td>
                <td className="px-4 py-3 text-sm text-gray-600">{enquiry.location}</td>
                <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                  {formatAmount(String(enquiry.targetPrice))}
                  <span className="block text-xs text-gray-500">
                    {optionLabel(propertyTypeOptions, enquiry.propertyType)}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{optionLabel(timeframeOptions, enquiry.timeframe)}</td>
                <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                  {new Date(enquiry.createdAt).toLocaleDateString()}
                </td>
              </tr>
            ))}
            {enquiries.length === 0 && (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  No enquiries yet
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-gray-500">
            Page {data.page + 1} of {data.totalPages} ({data.totalElements} total)
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="size-4" /> Previous
            </button>
            <button
              onClick={() => setPage((p) => p + 1)}
              disabled={data.last}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next <ChevronRight className="size-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
